import { useRef } from "preact/hooks";

import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import {
  RESET_REQUIRED_BLOCK_BODY,
  RESET_REQUIRED_BLOCK_BUTTON,
  RESET_REQUIRED_BLOCK_HEADING,
} from "../lib/authCopy";

export interface ResetRequiredBlockViewProps {
  /** Takes the person to the forced-change screen. */
  onContinue: () => void;
}

/**
 * TD-049 / TD-058 — UX §6.1 B2's full-screen block.
 *
 * ## Why this screen exists at all
 *
 * Until TD-049 the forced change was a CLIENT-SIDE route guard
 * (`app.tsx`), so a reset-required person could never SEE a refusal — the
 * app simply never rendered anything else. With the real server-side gate
 * (`app/api/v1/deps.py::_enforce_reset_scope`), every route outside the
 * three-entry allowlist now answers **403** while the flag is set, and any
 * request already in flight — a poll, a service-worker replay, a stale
 * mount — can land on one. This is what that looks like instead of a raw
 * "Forbidden."
 *
 * ## It is a queue, not a wall
 *
 * Every word and every ARIA choice here is UX §6.1 B2's, and the reasoning
 * is specific to this audience: a teenager who taps a normal button and is
 * met with a red assertive error has just been taught that they broke the
 * farm app. They did not. So:
 *
 * - `<h1>` and body, not an alert; nothing here is `role="alert"`.
 * - No `.error-hint`, no `is-destructive`, no warning iconography.
 * - The button is a plain forward action, not an acknowledgement.
 *
 * Do not restyle this toward the app's error treatment. That is a
 * regression, not a polish pass.
 *
 * The heading takes focus on mount, matching every other full-page auth
 * screen (`LoginView`, `CredentialSetupView`, `PasskeyEnrollView`).
 */
export function ResetRequiredBlockView({ onContinue }: ResetRequiredBlockViewProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useFocusHeadingOnMount(headingRef);

  return (
    <div class="logged-out-screen">
      <div class="auth-card">
        <span class="app-wordmark">Hayloft</span>
        <h1 ref={headingRef} tabIndex={-1} class="logged-out-heading">
          {RESET_REQUIRED_BLOCK_HEADING}
        </h1>
        <p class="auth-subheading">{RESET_REQUIRED_BLOCK_BODY}</p>
        <button type="button" class="primary-btn" onClick={onContinue}>
          {RESET_REQUIRED_BLOCK_BUTTON}
        </button>
      </div>
    </div>
  );
}
